import React, { useCallback, useContext, useEffect } from "react";
import { useI18n } from "../../hooks/i18n-hooks";
import VStack from "../../ui_atoms/VStack";
import PreIdVerificationImage from "../../assets/images/ill.svg";
import ScreenCard from "../ScreenCard";
import { StackScreenProps } from "@react-navigation/stack";
import { RootStackParamList } from "../../navigations/RootNavigation";
import { useNavigation } from "@react-navigation/native";
import { OnboardingPermitType, onboardingPermitTypeAtom } from "../../atoms/auth";
import { useRecoilValue } from "recoil";
import { ProfileType } from "../../__generated__/graphql";
import MarkdownView from "../../components/MarkdownView";
import { ProfileSwitcherManagerContext } from "../../contexts/ProfileSwitcherManager";
import { useIsScreenSizeMin } from "../../hooks/useIsScreenSizeMin";
import { TitleMediumText } from "../../components/FormattedText";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useStatusBarTranslucent } from "../../hooks/use-status-bar-translucent";
import { kycGradient } from "../../configs/linear-gradients";
import { useTracking } from "../../hooks/tracking";

const getI18nKeyForOnboardingPermitType = (
    profileType?: ProfileType,
    permitType?: OnboardingPermitType,
): string => {
    if (profileType === ProfileType.Client) {
        return "screens.auth.preIdVerification.description_client";
    }
    switch (permitType) {
        case "swiss_or_permit":
            return "screens.auth.preIdVerification.description_swiss_or_permit";
        case "no_permit":
            return "screens.auth.preIdVerification.description_no_permit";
        default:
            // if we lost the onboardingPermitType, we default to the client description
            return "screens.auth.preIdVerification.description_client";
    }
};

type ScreenParams = StackScreenProps<RootStackParamList>;

const PreIdVerification = () => {
    const i18n = useI18n();
    const insets = useSafeAreaInsets();
    const navigator = useNavigation<ScreenParams["navigation"]>();
    const onboardingPermitType = useRecoilValue(onboardingPermitTypeAtom);
    const { profileType } = useContext(ProfileSwitcherManagerContext);
    const isLarge = useIsScreenSizeMin("large");
    const { trackEvent } = useTracking();
    const next = useCallback(() => {
        navigator.navigate("VeriffScreen");
    }, [navigator]);

    useStatusBarTranslucent();

    useEffect(() => {
        void trackEvent("kyc_showed");
    }, [trackEvent]);

    return (
        <ScreenCard
            backgroundGradient={kycGradient}
            isContentContainerFlex
            buttons={[
                {
                    title: i18n.t("screens.auth.preIdVerification.nextButton"),
                    onPress: next,
                },
            ]}
        >
            <VStack
                gap={1}
                h="2/3"
                v={1.5}
                style={{
                    marginTop: insets.top,
                    marginBottom: 0,
                    paddingBottom: 0,
                }}
            >
                <TitleMediumText style={{ alignSelf: "center" }}>
                    {i18n.t("screens.auth.preIdVerification.title")}
                </TitleMediumText>
                <PreIdVerificationImage width="100%" style={{ overflow: "visible" }} />
                <MarkdownView
                    markdown={`${i18n.t("screens.auth.preIdVerification.description")}
                    <br>
                    ${i18n.t(
                        getI18nKeyForOnboardingPermitType(profileType, onboardingPermitType),
                    )}`}
                    markdownStyle={
                        profileType === ProfileType.Client || !onboardingPermitType
                            ? undefined
                            : { textgroup: { textAlign: "center" } }
                    }
                    style={isLarge ? { alignItems: "center" } : undefined}
                />
            </VStack>
        </ScreenCard>
    );
};

export default PreIdVerification;
